import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { getUser } from '../state/user';
import useTranslation from '../hooks/useTranslation';

const Context = createContext({ appcues: null, loading: false, error: null });

export const useAppcues = () => useContext(Context);

export const Provider = ({ children }) => {
  const [appcues, setAppcues] = useState<Window['Appcues']>(null);
  const user = useSelector(getUser);
  const { language } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    let timeout: ReturnType<typeof setTimeout>;

    // wait for Appcues to load
    const interval = setInterval(() => {
      if ('Appcues' in window) {
        clearInterval(interval);
        clearTimeout(timeout);
        setAppcues(window.Appcues);
        setLoading(false);
      }
    }, 300);

    // stop looking for Appcues if it cannot find it for a while
    timeout = setTimeout(() => {
      if (!('Appcues' in window)) {
        clearInterval(interval);
        setLoading(false);
        setError('Timedout loading Appcues');
      }
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  // track user
  useEffect(() => {
    if (!appcues) {
      return;
    }

    if (user.id) {
      appcues.identify(user.id, { language, email: user.email });
    } else {
      appcues.reset();
    }
  }, [appcues, user, language]);

  const value = useMemo(() => ({ appcues, loading, error }), [appcues, loading, error]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

Provider.propTypes = {
  children: PropTypes.node.isRequired,
};
